import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../primitives/tabs';
import { CalendarDatePickerV2 } from '../../calendar/calendar-date-picker-v2';
import { LabelWithoutRef } from '../../label/label-without-ref';
import { ScheduleDateOrRelative, ScheduleReference } from '../types';
import { fromAbsoluteDateString, toAbsoluteDateString } from '../utils/date-time';
import { describeDateOrRelative } from '../utils/describe-schedule';
import { isImmediate, isRelativeDate } from '../utils/relative-date';
import { IncrementsEditor } from './increments-editor';

type Mode = 'now' | 'absolute' | 'relative';

function getMode(
  value: ScheduleDateOrRelative | undefined,
  allowImmediate: boolean,
  reference: ScheduleReference,
): Mode {
  if (reference === 'previous' && typeof value === 'string') {
    return allowImmediate ? 'now' : 'relative';
  }
  if (typeof value === 'string') return 'absolute';
  if (isRelativeDate(value) && (!isImmediate(value) || !allowImmediate)) return 'relative';
  return allowImmediate ? 'now' : reference === 'previous' ? 'relative' : 'absolute';
}

interface RelativeDateFieldProps {
  /** Shows an immediate "Ahora" or "Anterior" shortcut for the selected reference. */
  allowImmediate?: boolean;
  disabled?: boolean;
  reference?: ScheduleReference;
  /** Describe the first previous-based action relative to its trigger, keeping the stored reference. */
  relativeToTrigger?: boolean;
  /** Minimum selectable day in "absolute" mode (e.g. today, or another field's resolved date). */
  fromDate?: Date;
  id?: string;
  label: string;
  timeZone: string;
  value: ScheduleDateOrRelative | undefined;
  onChange: (value: ScheduleDateOrRelative) => void;
}

/** Guided editor for a single {@link ScheduleDateOrRelative} field (used for executeAt/startsAt/endsAt). */
export function RelativeDateField({
  allowImmediate = true,
  disabled = false,
  reference = 'now',
  relativeToTrigger = false,
  fromDate,
  id,
  label,
  timeZone,
  value,
  onChange,
}: RelativeDateFieldProps) {
  const mode = getMode(value, allowImmediate, reference);
  const increments = isRelativeDate(value) ? (value.increments ?? []) : [];
  const absoluteDate =
    typeof value === 'string' ? fromAbsoluteDateString(value, timeZone) : undefined;

  function handleModeChange(nextMode: string) {
    if (nextMode === 'now') {
      onChange({ reference });
    } else if (nextMode === 'absolute') {
      onChange(toAbsoluteDateString(new Date(), timeZone));
    } else {
      onChange({ reference, increments: [{ value: 1, unit: 'days' }] });
    }
  }

  return (
    <div className="space-y-1">
      <LabelWithoutRef className="pt-0 text-xs font-medium text-muted-foreground">
        {label}
      </LabelWithoutRef>
      <Tabs className="[&>.tabs-content]:mt-2" value={mode} onValueChange={handleModeChange}>
        <TabsList className="rounded-[1.1rem] p-1">
          {allowImmediate && (
            <TabsTrigger
              className="px-3 py-1"
              disabled={disabled}
              id={id ? `${id}-now-tab` : undefined}
              value="now"
            >
              {relativeToTrigger
                ? 'Después de un accionador'
                : reference === 'previous'
                  ? 'Al finalizar la anterior'
                  : 'Ahora'}
            </TabsTrigger>
          )}
          {reference === 'now' && (
            <TabsTrigger
              className="px-3 py-1"
              disabled={disabled}
              id={id ? `${id}-absolute-tab` : undefined}
              value="absolute"
            >
              El
            </TabsTrigger>
          )}
          <TabsTrigger
            className="px-3 py-1"
            disabled={disabled}
            id={id ? `${id}-relative-tab` : undefined}
            value="relative"
          >
            Después de
          </TabsTrigger>
        </TabsList>

        {reference === 'now' && (
          <TabsContent value="absolute">
            <div className="rounded-[1.6rem] bg-background p-4 shadow-sm">
              <CalendarDatePickerV2
                date={absoluteDate}
                disabled={disabled}
                fromDate={fromDate}
                hasTime
                id={id ? `${id}-absolute` : undefined}
                mode="single"
                onChange={(day) => day && onChange(toAbsoluteDateString(day, timeZone))}
                timeZone={timeZone}
              />
            </div>
          </TabsContent>
        )}

        <TabsContent value="relative">
          <div className="space-y-4 rounded-[1.6rem] bg-background p-4 shadow-sm">
            <IncrementsEditor
              disabled={disabled}
              id={id ? `${id}-relative` : undefined}
              increments={increments}
              onChange={(nextIncrements) => onChange({ reference, increments: nextIncrements })}
            />
            {increments.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {describeDateOrRelative(
                  { reference, increments },
                  timeZone,
                  allowImmediate ? 'start' : 'end',
                  relativeToTrigger,
                )}
              </p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
